// ============================================================
// RUNTIME HEALTH MONITOR — Fase 11.3
// Monitoramento contínuo: memória, realtime, engines, throughput
// ============================================================
import { obs } from '@/core/observability'
import { checkStreamHealth } from '@/core/eventStreamProvider'

export interface HealthSnapshot {
  timestamp:       string
  memory:          MemoryHealth
  realtime:        RealtimeHealth
  engines:         EngineHealth
  observability:   ObsHealth
  overall:         'ok' | 'degraded' | 'critical'
}

interface MemoryHealth {
  heapUsedMB:      number
  heapLimitMB:     number
  usagePct:        number
  status:          'ok' | 'warning' | 'critical'
}

interface RealtimeHealth {
  ok:              boolean
  latencyMs:       number
  provider:        string
  status:          'ok' | 'degraded' | 'offline'
}

interface EngineHealth {
  twinRunning:     boolean
  visionRunning:   boolean
  obsBufferSize:   number
}

interface ObsHealth {
  bufferSize:      number
  status:          'ok' | 'warning'
}

// ─── Singleton monitor ────────────────────────────────────────
let _timer: ReturnType<typeof setInterval> | null = null
let _lastSnapshot: HealthSnapshot | null = null
const _listeners: Array<(snap: HealthSnapshot) => void> = []

export function startHealthMonitor(intervalMs = 30_000): () => void {
  if (_timer) clearInterval(_timer)

  _timer = setInterval(() => { void runHealthCheck() }, intervalMs)
  void runHealthCheck()

  return () => {
    if (_timer) { clearInterval(_timer); _timer = null }
  }
}

export function onHealthSnapshot(fn: (snap: HealthSnapshot) => void): () => void {
  _listeners.push(fn)
  return () => {
    const idx = _listeners.indexOf(fn)
    if (idx !== -1) _listeners.splice(idx, 1)
  }
}

export function getLastHealthSnapshot(): HealthSnapshot | null {
  return _lastSnapshot
}

async function runHealthCheck(): Promise<void> {
  const memory    = getMemoryHealth()
  const realtime  = await getRealtimeHealth()
  const engines   = getEngineHealth()
  const obsHealth = getObsHealth()

  const overall = deriveOverall(memory, realtime)

  const snap: HealthSnapshot = {
    timestamp:     new Date().toISOString(),
    memory,
    realtime,
    engines,
    observability: obsHealth,
    overall,
  }

  _lastSnapshot = snap
  _listeners.forEach(fn => fn(snap))

  if (overall !== 'ok') {
    obs.warn('health', `System health: ${overall}`, {
      heapPct:   memory.usagePct,
      rtLatency: realtime.latencyMs,
      rtStatus:  realtime.status,
    })
  }
}

// ─── Sub-checkers ─────────────────────────────────────────────
function getMemoryHealth(): MemoryHealth {
  if (typeof performance === 'undefined' || !(performance as any).memory) {
    return { heapUsedMB: 0, heapLimitMB: 0, usagePct: 0, status: 'ok' }
  }
  const mem = (performance as any).memory as {
    usedJSHeapSize: number
    jsHeapSizeLimit: number
  }
  const usedMB  = Math.round(mem.usedJSHeapSize  / 1_048_576)
  const limitMB = Math.round(mem.jsHeapSizeLimit / 1_048_576)
  const pct     = limitMB > 0 ? Math.round((usedMB / limitMB) * 100) : 0
  return {
    heapUsedMB:  usedMB,
    heapLimitMB: limitMB,
    usagePct:    pct,
    status:      pct >= 90 ? 'critical' : pct >= 70 ? 'warning' : 'ok',
  }
}

async function getRealtimeHealth(): Promise<RealtimeHealth> {
  try {
    const result = await checkStreamHealth()
    return {
      ok:        result.ok,
      latencyMs: result.latencyMs,
      provider:  result.provider,
      status:    !result.ok ? 'offline' : result.latencyMs > 2000 ? 'degraded' : 'ok',
    }
  } catch {
    return { ok: false, latencyMs: 0, provider: 'unknown', status: 'offline' }
  }
}

function getEngineHealth(): EngineHealth {
  return {
    twinRunning:   false,  // populated externally via registerEngineStatus()
    visionRunning: false,
    obsBufferSize: obs.bufferSize(),
  }
}

function getObsHealth(): ObsHealth {
  const size = obs.bufferSize()
  return { bufferSize: size, status: size > 200 ? 'warning' : 'ok' }
}

function deriveOverall(memory: MemoryHealth, realtime: RealtimeHealth): HealthSnapshot['overall'] {
  if (memory.status === 'critical' || realtime.status === 'offline') return 'critical'
  if (memory.status === 'warning'  || realtime.status === 'degraded') return 'degraded'
  return 'ok'
}
